<template>
  <ModalFullScreen
    v-model="open"
    fullscreen
    no-click-animation
    persistent>
    <ComponentLayout
      id="group-offer-different-price"
      fluid_main_container
      main_color="var(--color-main-green)">
      <template #breadcrumbs>
        <TenderBreadcrumbs
          :current="$t('Tenders.ToRequestNewPrice')"
          :dialog.sync="open"/>
      </template>
      <template #main_content>
        <div
          class="pr-8 mt-4 width-100">
          <div class="flex flex-between">
            <div class="text-left h1-headline">
              {{ $t('Tenders.ToRequestNewPrice') }} {{ $t('Common.For') }}
            </div>
            <SendOfferDifferentPrice
              :btn_text="$t('Tenders.SendPriceRequest')"
              :check-validation="checkValidation"
              class="pr-5"
              :disabled="send_disabled"
              :rows="send_model"
              :tender-id="tender.tenderId"
              @send="close"/>
          </div>
          <div class="pt-5">
            <sForm
              ref="form"
              :model="{items: groups}"
              :rules="rules">
              <div
                v-for="(group, gInd) in groups"
                :key="group.groupId"
                class="group-row">
                <div
                  class="group-lot-title"
                  data-qa="group-lot-title">
                  # {{ gInd + 1 }} {{ group.title }}
                </div>
                <div
                  v-for="(position, pInd) in group.positions"
                  :key="position.id"
                  :class="{'pb-4': pInd != group.positions.length - 1}">
                  <div
                    class="group-position-title"
                    data-qa="group-position-title">
                    <template v-if="group.positions.length > 1">
                      # {{ pInd + 1 }} {{ position.title }}
                    </template>
                    <template v-else>
                      # {{ gInd + 1 }} {{ position.flatTitle }}
                    </template>
                  </div>
                  <OfferDifferentPriceRadio
                    ref="setNewPrice"
                    class="px-3 py-2"
                    :currency="getCurrency(position)"
                    :error.sync="position.value_error"
                    :form-item="`items[${gInd}].positions[${pInd}].new_price`"
                    is-multiple
                    :is-sale="isSale"
                    :model_radio.sync="position.new_price_mode"
                    :model_text.sync="position.new_price"
                    @changePrice="(text, mode) => changePrice(position, text, mode)"/>
                  <v-data-table
                    :key="position.new_price + position.new_price_mode"
                    calculate-widths
                    class="offers-table offers "
                    :data-qa="position.id + '-offers-table'"
                    disable-pagination
                    :headers="headers"
                    hide-default-footer
                    item-key="index"
                    :items="position.items"
                    :no-data-text="$t('Tenders.NoData')">
                    <template #[`item.name`]="{ item }">
                      <VerificationStatus
                        v-if="item.bidderInfo && item.bidderInfo.verificationStatus == 'VERIFIED'"
                        class="mr-1"
                        :status="item.bidderInfo.verificationDecision"/>
                      <span> {{ item.bidderInfo ? item.bidderInfo.organizationName || '–' : '–' }} </span>
                      <div
                        v-if="item.bidderInfo && item.bidderInfo.rating != undefined "
                        class="opacity-6">
                        {{ $t('Tenders.Rating') }}: {{ item.bidderInfo.rating }}
                      </div>
                    </template>
                    <template #[`item.quantity`]="{ item }">
                      {{ item.selectedQuantity || item.quantity }} {{ (item.unit ? item.unit.title : '–') || '–' }}
                    </template>
                    <template #[`item.current_price`]="{ item }">
                      <ShowValueRoundingDigit
                        data-qa="current-price-value"
                        :minimum-fraction-digits="2"
                        :value="item.currencyPrice"/> {{ getCurrency(item) }}
                    </template>
                    <template #[`item.new_price`]="{ item, index }">
                      <sFormItem
                        v-if="(item.new_price || item.difference) && ( !item.edit_price || (position.new_price_mode == 1 || position.new_price_mode == 2))"
                        ref="input"
                        class="mb-0 width-100"
                        :prop="`items[${gInd}].positions[${pInd}].items[${index}].difference`">
                        <div class="flex gap-5 align-center">
                          <div class="h7-text-bold">
                            <ShowValueRoundingDigit
                              data-qa="new-price-value"
                              :minimum-fraction-digits="2"
                              :value="item.new_price"/>
                          </div>
                          <sButton
                            v-if="position.new_price_mode == 'PRICE' || position.new_price_mode == 'PERSENT'"
                            icon="edit"
                            small
                            type="text"
                            @click.stop="item.edit_price = true"/>
                        </div>
                      </sFormItem>
                      <template v-else>
                        <OfferDifferentPriceInput
                          ref="bid_input"
                          :currency="getCurrency(item)"
                          :form-item="`items[${gInd}].positions[${pInd}].items[${index}].difference`"
                          :type.sync="position.new_price_mode"
                          :value.sync="item.input"
                          @blur="text => closeEditing(text, item, position.new_price_mode)"
                          @change="text => changeBidPrice(item, text, position.new_price_mode )"/>
                      </template>
                    </template>
                    <template #[`item.new_total`]="{ item, index }">
                      <div class="flex flex-between align-center">
                        <ShowValueRoundingDigit
                          data-qa="new-total-value"
                          :maximum-fraction-digits="2"
                          :minimum-fraction-digits="2"
                          :value="(item.selectedQuantity || item.quantity) * item.new_price"/> {{ getCurrency(item) }}
                        <sButton
                          icon="cross"
                          type="text"
                          @click="deleteItem(item, index, position, group)"/>
                      </div>
                    </template>
                    <template #[`item.comment`]="{ item }">
                      <template v-if="item.comment && !item.edit_comment">
                        <span class="h7-text-bold">
                          {{ item.comment }}
                        </span>
                        <sButton
                          icon="edit"
                          small
                          type="text"
                          @click.stop="item.edit_comment = true"/>
                      </template>
                      <template v-else>
                        <sInput
                          clearable
                          data-qa="lot-comment"
                          :max-length="254"
                          :placeholder="$t('Common.OptionalComment')"
                          :value.sync="item.comment"
                          @blur="item.comment ? item.edit_comment = false : ''"
                          @keydown_enter="item.comment ? item.edit_comment = false : ''"
                          @keydown_esc="item.comment ? item.edit_comment = false : ''"/>
                      </template>
                    </template>
                  </v-data-table>
                </div>
              </div>
            </sForm>
          </div>
        </div>
      </template>
      <template #footer>
        <BackToTenderBtn
          key="footer"
          class="ml-auto"
          @click="close"/>
      </template>
    </ComponentLayout>
  </ModalFullScreen>
</template>

<script>
import ComponentLayout from 'pages/templates/ComponentLayout.vue'
import BackToTenderBtn from 'components/tenders/BackToTenderBtn.vue'
import TenderBreadcrumbs from 'components/tenders/TenderBreadcrumbs.vue'
import VerificationStatus from 'components/tenders/VerificationStatus.vue'
import ShowValueRoundingDigit from 'components/common/blocks/ShowValueRoundingDigit.vue'
import SendOfferDifferentPrice from 'components/tenders/tender page/offer different price/SendOfferDifferentPrice.vue'
import OfferDifferentPriceRadio from 'components/tenders/tender page/offer different price/OfferDifferentPriceRadio.vue'
import OfferDifferentPriceInput from 'components/tenders/tender page/offer different price/OfferDifferentPriceInput.vue'

import ModalFullScreen from 'components/common/elements/ModalFullScreen.vue'

import { modalMixin } from 'mixins'

export default {
  name: 'groupOfferDifferentPrice',
  mixins: [ modalMixin ],
  data() {
    return {
      rules: {},
      currencies: [],
      groups: []
    }
  },
  props: {
    dialog: Boolean,
    tender: Object,
    elements: Array,
    validateInput: Function,
    getNewPrice: Function,
    isSale: Boolean
  },
  async created() {
    await this.$store.dispatch('setCurrencies')
      .then(res => this.currencies = res)
  },
  computed: {
    open: {
      get() {
        return this.dialog
      },
      set(value) {
        if ( !value ) {
          this.$emit('close')
        }
        this.$emit('update:dialog', value)
      }
    },
    headers() {
      return [
        { text: this.$t('Tenders.Supplier') , value: 'name', },
        { text: this.$t('Catalog.Amount'), value: 'quantity', width: '15%' , sortable: false},
        { text: this.$t('Tenders.CurrentSupplierPrice'), value: 'current_price', width: '15%', sortable: false, class: 'header_break-spaces' },
        { text: this.$t('Tenders.NewPrice'), value: 'new_price', width: '15%', sortable: false },
        { text: this.$t('Suppliers.Comment'), value: 'comment', width: '20%', sortable: false },
        { text: this.$t('Tenders.NewTtotal'), value: 'new_total', width: '15%', sortable: false },
      ]
    },
    send_disabled() {
      return this.groups.some(group => group.positions.some(position => position.value_error || position.items.some(el => !el.new_price))) && this.$t('Tenders.NewPriceForAllSelectedOffers')
    },
    send_model() {
      let items = []
      this.groups.forEach(group => {
        group.positions.forEach(position => {
          items.push(...position.items.map(bid => ({
            bidItemId: bid.id ,
            price: bid.new_price,
            bidder: bid.org_num,
            comment: bid.comment
          })) )
        })
      })
      return items
    }
  },
  methods: {
    close() {
      this.open = false
      this.groups = []
      this.$emit('close')
    },
    setItems() {
      this.setGroups()
    },
    setRules() {
      const that = this
      let getRule = (type, required = true, lotId, newPriceMode, priceObject) => {
        return {
          [type]: {
            trigger: 'change',
            validator: (rule, value, callback) => {
              return that.validateInput(rule, priceObject, callback, newPriceMode, required, lotId)
            },
          },
        }
      }
      let getPositionRules = (position, gInd, pInd) => {
        let pref = `items[${gInd}].positions[${pInd}]`
        const newPriceMode = position.new_price_mode
        let position_rules = {}
        position.items.forEach((bid, ind) => {
          const budRuleName = `${pref}.items[${ind}].difference`

          const priceObject = {
            price: bid.new_price,
            percent: newPriceMode === 'PRICE' ? null : bid.input
          }

          position_rules = {
            ...position_rules,
            ...getRule(budRuleName, true, bid.lotId, newPriceMode, priceObject)
          }
        })
        return position_rules
      }

      this.groups.forEach((group, gInd) => {
        group.positions.forEach((position, pInd) => {
          this.rules = {
            ...this.rules,
            ...getPositionRules(position, gInd, pInd)
          }
        })
      })
    },
    setGroups() {
      let groups_obj = this.$_.groupBy(
        this.$_.orderBy(this.elements, ['lotGroupNumber'], ['asc']), 
        el => el.groupId != null ? `group_${el.groupId}` : `lot_${el.lotId}`
      )

      this.groups = Object.entries(groups_obj).map(([groupId, items]) => {
        let positions_obj = this.$_.groupBy(items, 'lotId')
        let positions = Object.entries( positions_obj ).map(([lotId, positionItems]) => {
          let curr = this.$_.groupBy(positionItems, 'currencyId')
          curr = Object.keys( curr )?.length > 1 ? '' : Object.keys( curr )[0]
          return {
            value_error: '',
            new_price: null,
            new_price_mode: 'PRICE',
            currencyId: curr,
            items: positionItems.map(el => ({
              ...el,
              new_price: '',
              difference: '',
              edit_price: true,
              edit_comment: true,
              comment: '',
              input: ''
            })),
            id: lotId,
            title: positionItems[0]?.lotTitleOrig || positionItems[0]?.lotTitle,
            flatTitle: positionItems[0]?.lotTitle,
            lotNumber: positionItems[0]?.lotNumber
          }
        })
        return {
          groupId,
          title: items[0]?.lotGroupTitle,
          positions: positions.sort((a, b) => a.lotNumber - b.lotNumber)
        }
      })
    },
    deleteItem(item, ind, position, group) {
      let index = this.elements.findIndex( el => el.idBidRow == item.idBidRow)
      // eslint-disable-next-line vue/no-mutating-props
      this.elements.splice(index, 1)

      if ( !this.elements?.length ) {
        this.close()
      } else {
        position.items.splice(ind, 1)
        group.positions = group.positions.filter(el => el.items?.length)
        this.groups = this.groups.filter(el => el.positions?.length)
      }

    },
    changeBidPrice(item, text, mode, edit_price) {
      if (mode == 'PRICE') {
        item.new_price = text
        item.difference = text
        if (text && edit_price) {
          item.edit_price = false
        }
      } else if (text) {
        item.new_price = this.getNewPrice(item.currencyPrice, text, mode)
        item.difference = text
        if (edit_price) {
          item.edit_price = false
        }
      } else {
        item.new_price = null
        item.difference = null
      }
      item.input = text
    },
    changePrice(position, text, mode) {
      position.items.forEach(element => {
        this.changeBidPrice(element, text , mode, true)
      })
    },
    closeEditing(text, item, mode) {
      this.changeBidPrice(item, text, mode)
      if (text) {
        item.edit_price = false
      }
    },
    async checkValidation() {
      await this.setRules()

      let res = await this.$refs.form.validate().catch(() => {
        if (this.isSale) {
          return
        }
        let has_persent_error = this.groups.some(group => group.positions.some(position => {
          return position?.new_price_mode == 'PERSENT' && (position.new_price > 99.99 || position.items.some(it => it.new_price > 99.99))
        }))
        if ( has_persent_error ) {
          this.$notice.info({
            title: '',
            message: this.$t('Tenders.TheDiscountCannotBeMoreThan99'),
            showClose: true,
            progress: true,
            duration: 20
          })
        }
      })
      return res
    },
  },
  components: {
    ModalFullScreen,
    BackToTenderBtn,
    ComponentLayout,
    TenderBreadcrumbs,
    VerificationStatus,
    ShowValueRoundingDigit,
    SendOfferDifferentPrice,
    OfferDifferentPriceInput,
    OfferDifferentPriceRadio
  }
}
</script>
<style scoped lang="scss">
.group-row {
  &:not(:nth-child(1)) {
    margin-top: 14px;
  }
  & > div:nth-child(2) > .group-position-title:nth-child(1) {
    border-radius: 0px 0px 8px 8px;
  }
}

.group-lot-title,
.group-position-title {
text-align: left;  padding-top: 16px;
  padding-bottom: 16px;
  padding-right: 16px;
  padding-left: 16px;
}

.group-lot-title {
  background-color: var(--v-main__light_green-base);
  border-radius: 8px 8px 0px 0px;
}
.group-position-title {
  background-color: var(--alert-info-background);
  border-radius: 8px;
}
</style>
